import dayjs from 'dayjs';
import type { ChatHistory } from '../../containers/flora/flora.type';

type GroupedHistories = {
  label: string;
  histories: ChatHistory[];
};

/**
 * Parses a UTC-naive ISO timestamp from the backend (e.g. "2024-03-15T10:00:00",
 * no offset suffix) into a JS Date representing the correct UTC instant.
 *
 * Without the 'Z' suffix JS would parse the string as local time and silently
 * shift the instant by the host offset.
 *
 * @param {string} isoUtcNaive - Backend timestamp string without offset suffix
 * @returns {Date} A Date representing the same instant, parsed as UTC
 */
const parseUtcNaiveTimestamp = (isoUtcNaive: string): Date => new Date(`${isoUtcNaive}Z`);

/**
 * Groups chat histories by date and returns them with descriptive labels.
 *
 * Calendar-day boundaries are evaluated in the user's local timezone (by
 * comparing local midnights, not elapsed 24-hour windows) so that a chat
 * created at 23:00 local time labels as "Yesterday" the next morning, even
 * when the underlying UTC instant has already rolled into the next UTC day.
 *
 * @param {ChatHistory[]} histories - Array of chat histories to be grouped
 * @returns {GroupedHistories[]} Grouped histories with labels like "Today", "Yesterday", "X days ago", etc.
 */
const groupHistoriesByDate = (histories: ChatHistory[]): GroupedHistories[] => {
  const now = new Date();
  const groups: GroupedHistories[] = [];

  const sortedHistories = [...histories].sort(
    (a, b) => parseUtcNaiveTimestamp(b.last_accessed).getTime() - parseUtcNaiveTimestamp(a.last_accessed).getTime(),
  );

  sortedHistories.forEach((history) => {
    const date = parseUtcNaiveTimestamp(history.last_accessed);
    const diffInDays = Math.max(0, Math.round(dayjs(now).startOf('day').diff(dayjs(date).startOf('day'), 'day', true)));
    const label = getLabelForDateDifference(diffInDays, date);

    const existingGroup = groups.find((g) => g.label === label);
    if (existingGroup) {
      existingGroup.histories.push(history);
    } else {
      groups.push({ label, histories: [history] });
    }
  });

  return groups;
};

/**
 * Gets a human-readable label describing how long ago a date was.
 *
 * @param {number} daysDiff - Number of days between the date and now
 * @param {Date} date - The date to get the label for
 * @returns {string} A string label like "Today", "Yesterday", "X days ago", "Last week", etc.
 */
const getLabelForDateDifference = (daysDiff: number, date: Date): string => {
  if (daysDiff === 0) {
    return 'Today';
  } else if (daysDiff === 1) {
    return 'Yesterday';
  } else if (daysDiff < 7) {
    return `${daysDiff} days ago`;
  } else if (daysDiff < 30) {
    const weeksDiff = Math.floor(daysDiff / 7);
    return weeksDiff === 1 ? 'Last week' : `${weeksDiff} weeks ago`;
  } else if (daysDiff < 365) {
    const monthsDiff = Math.floor(daysDiff / 30);
    return monthsDiff === 1 ? 'Last month' : `${monthsDiff} months ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
};

export { getLabelForDateDifference, groupHistoriesByDate };
